import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { ButtonBase } from '@material-ui/core';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { ResourceRequestStatus } from '~/types';
import {
  ResourceRequestStatusIndicator,
  ResourceMatchItem
} from '~/modules/resourcing/common/components';
import { RESOURCE_MATCH_ITEM_BUTTONS } from '~/modules/resourcing/common/components/ResourceMatchItem/enums';
import {
  useSelectedResourceRequestContext,
  useResourcingChartToolbarContext
} from '~/modules/projects/resourcing-plan/hooks';
import {
  ResourceRequestMatchItem,
  ResourceRequestRowDetails
} from './components';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100%',
    paddingRight: theme.spacing(1)
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  column: {
    flexGrow: 1,
    flexBasis: 100,
    alignSelf: 'stretch',
    padding: 0,
    margin: [[0, 0, 0, -1]],
    display: 'flex',
    textAlign: 'left',
    justifyContent: 'center',
    alignItems: 'flex-start',
    '&:last-child': {
      borderRight: 'none'
    },
    minWidth: 100
  },
  columnWithFF: {
    display: 'flex',
    alignSelf: 'stretch',
    minWidth: theme.spacing(11.75)
  },
  clickableColumn: {
    cursor: 'pointer',
    '&:hover': {
      background: alpha(theme.palette.action.active, 0.15)
    }
  },
  actionsColumn: {
    flexGrow: 0,
    flexShrink: 0,
    minWidth: theme.spacing(9),
    flexBasis: theme.spacing(9)
  },
  actions: {
    flexBasis: theme.spacing(8),
    flexGrow: 0,
    overflow: 'visible',
    padding: theme.spacing(1, 0, 0, 0.25)
  },
  actionsWithFF: {
    display: 'flex',
    alignItems: 'start',
    flexBasis: theme.spacing(8),
    overflow: 'visible',
    padding: theme.spacing(1, 0, 0, 0.25)
  },
  button: {
    width: '100%',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  },
  resourceRequestDetails: {
    paddingTop: theme.spacing(0.5)
  },
  resourceRequestRowDetailsContentMinHeight: {
    minHeight: theme.spacing(5)
  }
}));

const useStatusIndictorStyles = makeStyles(theme => ({
  root: {
    flexBasis: theme.spacing(0.5),
    width: theme.spacing(0.5),
    height: '100%'
  }
}));

const useDelaStyles = makeStyles(theme => ({
  aiColumn: {
    background: theme.palette.Dela.lightBackground,
    '&:hover > div': {
      background: 'transparent'
    }
  }
}));

const useResourceMatchItemStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(0.5, 0),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(0.5)
  }
}));

export const ResourceRequestItem = ({
  actions,
  resourceRequest,
  isPsaRmpTaskAllocation1Enabled,
  maxSkillLevel,
  isPsaPraaResourcingUIAndRMRole,
  setAnchorEl,
  setPopoverResourceRequest,
  isPsaPraaResourceMatchingEnabled,
  project
}) => {
  const classes = useStyles();
  const resourceMatchItemClasses = useResourceMatchItemStyles();
  const statusIndictorClasses = useStatusIndictorStyles();
  const delaClasses = useDelaStyles();
  const selectedResourceRequestContext = useSelectedResourceRequestContext();
  const {
    displayUnit,
    scale,
    isRmp2025Q4Enhancements
  } = useResourcingChartToolbarContext();

  const openDrawer = useCallback(() => {
    const { openResourceRequestDrawer } = selectedResourceRequestContext;

    openResourceRequestDrawer({ resourceRequestId: resourceRequest.id });
  }, [selectedResourceRequestContext, resourceRequest.id]);

  const isPsaPraaResourceMatchingAndTaskAllocationEnabled =
    isPsaPraaResourceMatchingEnabled && isPsaRmpTaskAllocation1Enabled;

  return isPsaPraaResourceMatchingAndTaskAllocationEnabled ? (
    <div className={classnames(classes.root, classes.flexRow)}>
      {isPsaPraaResourceMatchingAndTaskAllocationEnabled ? (
        <div className={classes.columnWithFF}>
          <ResourceRequestStatusIndicator
            classes={statusIndictorClasses}
            status={resourceRequest.requestStatus}
          />
          {actions && (
            <div data-qe-id="RowTotalAction" className={classes.actionsWithFF}>
              {actions}
            </div>
          )}
        </div>
      ) : null}
      <div
        className={classnames(classes.flexColumn, {
          [classes.resourceRequestDetails]:
            resourceRequest.requestStatus === ResourceRequestStatus.Submitted
        })}
      >
        <div
          className={classnames(
            classes.flexRow,
            classes.resourceRequestRowDetailsContentMinHeight
          )}
        >
          <ButtonBase
            data-qe-id="ResourceRequestRow"
            className={classnames({
              [classes.button]: isPsaRmpTaskAllocation1Enabled
            })}
            onClick={openDrawer}
          >
            <ResourceRequestRowDetails
              maxSkillLevel={maxSkillLevel}
              resourceRequest={resourceRequest}
              allocations={resourceRequest?.resourceAllocations?.filter(ra =>
                Boolean(ra.user && ra.user.userUri !== null)
              )}
              showResourceAllocation
              isPsaRmpTaskAllocation1Enabled={isPsaRmpTaskAllocation1Enabled}
              isRmp2025Q4Enhancements={isRmp2025Q4Enhancements}
              isPsaPraaResourcingUIAndRMRole={isPsaPraaResourcingUIAndRMRole}
              isPsaPraaResourceMatchingEnabled={
                isPsaPraaResourceMatchingEnabled
              }
            />
          </ButtonBase>
        </div>
        {isPsaPraaResourcingUIAndRMRole &&
        resourceRequest.requestStatus === ResourceRequestStatus.Submitted ? (
          <ResourceMatchItem
            classes={resourceMatchItemClasses}
            resourceRequest={resourceRequest}
            buttonType={RESOURCE_MATCH_ITEM_BUTTONS.ACCEPT}
            setAnchorEl={setAnchorEl}
            setPopoverResourceRequest={setPopoverResourceRequest}
            project={project}
            narrow
          />
        ) : null}
      </div>
    </div>
  ) : (
    <>
      <div
        className={classnames({
          [classes.column]: !isPsaRmpTaskAllocation1Enabled,
          [classes.columnWithFF]: isPsaRmpTaskAllocation1Enabled,
          [classes.actionsColumn]: !isPsaRmpTaskAllocation1Enabled,
          [delaClasses.aiColumn]:
            !isPsaPraaResourceMatchingEnabled &&
            resourceRequest.isDelaDraftResourceRequest
        })}
      >
        <ResourceRequestStatusIndicator
          classes={isPsaRmpTaskAllocation1Enabled && statusIndictorClasses}
          status={resourceRequest.requestStatus}
        />
        {actions && (
          <div
            data-qe-id="RowTotalAction"
            className={classnames({
              [classes.actions]: !isPsaRmpTaskAllocation1Enabled,
              [classes.actionsWithFF]: isPsaRmpTaskAllocation1Enabled
            })}
          >
            {actions}
          </div>
        )}
      </div>

      <ButtonBase
        data-qe-id="ResourceRequestRow"
        className={classnames({
          [classes.button]: isPsaRmpTaskAllocation1Enabled,
          [classes.column]: !isPsaRmpTaskAllocation1Enabled,
          [classes.clickableColumn]: !isPsaRmpTaskAllocation1Enabled,
          [delaClasses.aiColumn]:
            !isPsaPraaResourceMatchingEnabled &&
            resourceRequest.isDelaDraftResourceRequest
        })}
        onClick={openDrawer}
      >
        <ResourceRequestRowDetails
          maxSkillLevel={maxSkillLevel}
          resourceRequest={resourceRequest}
          allocations={resourceRequest?.resourceAllocations?.filter(ra =>
            Boolean(ra.user && ra.user.userUri !== null)
          )}
          showResourceAllocation
          isPsaRmpTaskAllocation1Enabled={isPsaRmpTaskAllocation1Enabled}
          isRmp2025Q4Enhancements={isRmp2025Q4Enhancements}
          isPsaPraaResourcingUIAndRMRole={isPsaPraaResourcingUIAndRMRole}
        />
      </ButtonBase>
      {isPsaPraaResourcingUIAndRMRole &&
        resourceRequest.requestStatus === ResourceRequestStatus.Submitted && (
          <ResourceRequestMatchItem
            classes={resourceMatchItemClasses}
            resourceRequest={resourceRequest}
            maxSkillLevel={maxSkillLevel}
            setAnchorEl={setAnchorEl}
            setPopoverResourceRequest={setPopoverResourceRequest}
            scale={scale}
            displayUnit={displayUnit}
            project={project}
            isPsaPraaResourceMatchingEnabled={isPsaPraaResourceMatchingEnabled}
          />
        )}
    </>
  );
};

ResourceRequestItem.propTypes = {
  actions: PropTypes.node.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  isPsaRmpTaskAllocation1Enabled: PropTypes.bool,
  maxSkillLevel: PropTypes.number,
  isPsaPraaResourcingUIAndRMRole: PropTypes.bool,
  setAnchorEl: PropTypes.func.isRequired,
  setPopoverResourceRequest: PropTypes.func.isRequired,
  isPsaPraaResourceMatchingEnabled: PropTypes.bool,
  project: PropTypes.object.isRequired
};

export default ResourceRequestItem;
